import { Box, Slide } from '@mui/material';
import { useAtomValue } from 'jotai';
import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useEntitySecurityChain, useEntitySecurityPath, useFeatureSecurityChain } from '../../../auth/Security';
import { useSidePanelSx } from '../../../components/layout/WithSidePanel/WithSidePanel';
import { useDeepLinkRuntimeState } from '../../../deepLink';
import { useIsWithinBreakpoints } from '../../../hooks/useIsWithinBreakpoints';
import { H, V } from '../../../Layout';
import { useStyling } from '../../../Theme';
import { SIDE_PANEL_CLOSE_REASONS } from '../../constants';
import { SidePanelSurfaceConflictError } from '../../errors';
import type { SidePanelDeclaration, SidePanelSubject } from '../../types';
import { SidePanelOwnerContext } from '../contexts/SidePanelOwnerContext';
import { SidePanelSurfaceContext } from '../contexts/SidePanelSurfaceContext';
import { useInternalSidePanelSystem } from '../hooks/useInternalSidePanelSystem';
import { useSidePanelCommands } from '../hooks/useSidePanelCommands';
import { contributionsAtom, layoutAtom, sessionAtom, surfaceAtom } from '../state/atoms';
import { dispatchOwnerNotification } from '../state/dispatchOwnerNotification';
import { SidePanelSlot } from './internal/SidePanelSlot';
import { SidePanelToolbarActions } from './SidePanelToolbarActions';

export type SidePanelLayoutProps = {
    /** Page-level panel declarations available to descendant commands and the header toolbar. */
    panels: readonly SidePanelDeclaration[];
    /** Default entity subject for panel commands. Defaults to null when the page has no entity subject. */
    subject?: SidePanelSubject | null;
    /** Page content, kept mounted but hidden and inert while a panel is open on small screens. */
    children: ReactNode;
    /** Content rendered on the left side of the header. */
    leftToolbar?: ReactNode;
    /** Content rendered before the panel actions on the right side of the header. */
    rightToolbar?: ReactNode;
    /** Hides the entire header, including toolbar content and panel actions. Defaults to false. */
    hideHeader?: boolean;
    /** MUI sx overrides applied after the active panel frame's default and expanded-layout styles. */
    panelBoxSx?: object;
    /** Optional bridge for legacy callers that control panel visibility outside the centralized system. */
    controlled?: {
        /** Requested panel id. Undefined requests closing the session owned by this surface. */
        visiblePanelId?: string;
        /** Receives visibility changes from panel commands; undefined indicates that the panel closed. */
        onVisiblePanelChange: (panelId: string | undefined) => void;
    };
};

/**
 * Registers a page's side-panel surface and renders its active panel beside the page content.
 *
 * @remarks
 * Requires an ancestor SidePanelSystemProvider. Only one surface may register in a system at a time;
 * a conflicting surface renders only its children. Descendant owners may contribute additional panels.
 * Page content stays mounted when a panel opens and becomes hidden and inert on small screens.
 */
export function SidePanelLayout({
    panels,
    subject = null,
    children,
    leftToolbar,
    rightToolbar,
    hideHeader = false,
    panelBoxSx = {},
    controlled
}: SidePanelLayoutProps) {
    const ownerId = useId();
    const { store, commandHandlers } = useInternalSidePanelSystem();
    const entitySecurityChain = useEntitySecurityChain();
    const featureSecurityChain = useFeatureSecurityChain();
    const deepLinkBasePath = useEntitySecurityPath();
    const { clearDeepLinkRuntimeState } = useDeepLinkRuntimeState();
    const session = useAtomValue(sessionAtom, { store });
    const contributions = useAtomValue(contributionsAtom, { store });
    const layout = useAtomValue(layoutAtom, { store });
    const sidePanelSx = useSidePanelSx(layout.isExpanded);
    const { theme } = useStyling();
    const { open, close } = useSidePanelCommands();
    const isSmallScreen = useIsWithinBreakpoints(['xs', 's']);
    const [surfaceConflict, setSurfaceConflict] = useState(false);
    const ownsSurface = useRef(false);

    const ownerRegistration = useMemo(
        () => ({ ownerId, declarations: panels, entitySecurityChain, featureSecurityChain, deepLinkBasePath }),
        [deepLinkBasePath, entitySecurityChain, featureSecurityChain, ownerId, panels]
    );
    const ownerValue = useMemo(
        () => ({ ...ownerRegistration, ownerChain: [ownerId], ownerRegistrations: [ownerRegistration] }),
        [ownerId, ownerRegistration]
    );

    // Register or refresh this page's surface before paint; reject a different surface already using the system.
    useLayoutEffect(() => {
        const currentSurface = store.get(surfaceAtom);
        if (currentSurface && currentSurface.ownerId !== ownerId) {
            setSurfaceConflict(true);
            if (process.env.NODE_ENV !== 'production') {
                console.error(new SidePanelSurfaceConflictError());
            }
            return;
        }
        ownsSurface.current = true;
        setSurfaceConflict(false);
        store.set(surfaceAtom, {
            ...ownerRegistration,
            subject,
            onVisiblePanelChange: controlled?.onVisiblePanelChange
        });
    }, [controlled?.onVisiblePanelChange, ownerId, ownerRegistration, store, subject]);

    // Release the surface in the layout phase so route replacements can register before passive cleanup runs.
    // Clear the active session, its runtime deep link, and expansion state, and notify its owner on teardown.
    useLayoutEffect(
        () => () => {
            if (!ownsSurface.current) return;
            const activeSession = store.get(sessionAtom);
            if (activeSession?.deepLinkPath) {
                clearDeepLinkRuntimeState(activeSession.deepLinkPath);
            }
            if (activeSession) {
                dispatchOwnerNotification(
                    activeSession,
                    'panel.closed',
                    { panelId: activeSession.panelId, reason: SIDE_PANEL_CLOSE_REASONS.surfaceUnmounted },
                    commandHandlers
                );
            }
            store.set(surfaceAtom, null);
            store.set(sessionAtom, null);
            store.set(layoutAtom, { isExpanded: false });
            ownsSurface.current = false;
        },
        [clearDeepLinkRuntimeState, commandHandlers, store]
    );

    // Bridge externally controlled visibility to commands without closing a session contributed by another owner.
    useEffect(() => {
        if (!controlled) return;
        const surfaceSessionId = session?.ownerId === ownerId ? session.panelId : undefined;
        if (controlled.visiblePanelId === surfaceSessionId) return;
        if (controlled.visiblePanelId) open(controlled.visiblePanelId);
        else if (surfaceSessionId) close();
    }, [close, controlled, open, ownerId, session?.ownerId, session?.panelId]);

    const declaration =
        panels.find(({ definition }) => definition.id === session?.panelId) ??
        contributions.get(session?.ownerId ?? '')?.find(({ definition }) => definition.id === session?.panelId);
    const PanelComponent = declaration?.definition.Component;

    if (surfaceConflict) {
        return <>{children}</>;
    }

    return (
        <SidePanelSurfaceContext.Provider value>
            <SidePanelOwnerContext.Provider value={ownerValue}>
                <V className="sidePanelLayout" fill sx={{ gap: 1, minHeight: 0, overflow: 'hidden' }}>
                    {!hideHeader && (
                        <H sx={{ alignItems: 'center' }}>
                            <H grow>{leftToolbar}</H>
                            <H sx={{ pr: 1, gap: 2, alignItems: 'center', justifyContent: 'flex-end', width: 'unset' }}>
                                <H>{rightToolbar}</H>
                                <SidePanelToolbarActions declarations={panels} subject={subject} />
                            </H>
                        </H>
                    )}
                    <H grow sx={{ gap: 1, minHeight: 0, overflow: 'hidden', position: 'relative' }}>
                        <Box
                            aria-hidden={Boolean(session && isSmallScreen)}
                            inert={Boolean(session && isSmallScreen)}
                            sx={{
                                alignSelf: 'stretch',
                                display: 'flex',
                                flexDirection: 'column',
                                flexGrow: 1,
                                minHeight: 0,
                                minWidth: 0,
                                overflow: 'auto',
                                visibility: session && isSmallScreen ? 'hidden' : 'visible'
                            }}
                        >
                            {children}
                        </Box>
                        <Slide
                            direction="left"
                            in={Boolean(session && PanelComponent)}
                            appear
                            mountOnEnter
                            unmountOnExit
                        >
                            <Box
                                data-testid="side-panel-frame"
                                sx={{
                                    bgcolor: theme.palette.background.paper,
                                    borderRadius: 1,
                                    flexShrink: 0,
                                    overflow: 'hidden',
                                    ...sidePanelSx,
                                    ...panelBoxSx
                                }}
                            >
                                {session && PanelComponent && (
                                    <SidePanelSlot
                                        key={session.sessionId}
                                        session={session}
                                        Component={PanelComponent}
                                        options={declaration?.options}
                                    />
                                )}
                            </Box>
                        </Slide>
                    </H>
                </V>
            </SidePanelOwnerContext.Provider>
        </SidePanelSurfaceContext.Provider>
    );
}
